import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, InputGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { TaskFilters, TaskStatus } from '../../services/taskService';
import debounce from 'lodash/debounce';

interface TaskFilterBarProps {
  initialFilters: TaskFilters;
  onFilterChange: (newFilters: Partial<TaskFilters>) => void;
  activeView: string; // Kept for now, might remove if no other filters depend on it
}

const TaskFilterBar: React.FC<TaskFilterBarProps> = ({ initialFilters, onFilterChange, activeView }) => {
  const [searchTerm, setSearchTerm] = useState(initialFilters.search || '');
  const [distance, setDistance] = useState(initialFilters.distance || '');

  // Debounce the search term update
  const debouncedFilterChange = useCallback(
    debounce((filtersToUpdate: Partial<TaskFilters>) => {
      onFilterChange(filtersToUpdate);
    }, 500), // 500ms debounce time
    [onFilterChange]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    debouncedFilterChange({ search: newSearchTerm || undefined });
  };

  // Handle direct filter changes
  const handleDirectFilterChange = (key: keyof TaskFilters, value: string | undefined) => {
    if (key === 'distance') {
        setDistance(value || '');
    }
    onFilterChange({ [key]: value || undefined });
  };

  return (
    <div className="filter-bar bg-light p-3 rounded mb-4">
      <Row className="g-2 align-items-center">
        {/* Search Input */}
        <Col md={8} lg={9}>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search title, description, skills..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
        </Col>

        {/* Distance Filter */}
        <Col md={4} lg={3}>
          <Form.Select 
            aria-label="Distance Filter" 
            value={distance} 
            onChange={(e) => handleDirectFilterChange('distance', e.target.value || undefined)}
          >
            <option value="">Any Distance</option>
            <option value="5">Within 5 km</option>
            <option value="10">Within 10 km</option>
            <option value="25">Within 25 km</option>
            <option value="50">Within 50 km</option>
            <option value="remote">Remote Only</option>
          </Form.Select>
        </Col>
      </Row>
    </div>
  );
};

export default TaskFilterBar; 